import { useEffect, useRef, useState } from "react"
import AnimatedHeader from "./AnimatedHeader"
import CaptureControls from "./CaptureControls"
import { dataURLtoFile } from "utils/imageConverter"
import {
  Wrapper,
  VideoElement,
  HiddenCanvas,
  ErrorContainer,
  ErrorTitle,
  ErrorText,
  FlashOverlay,
} from "./styled"
import { useBackToHome } from "features/meal/hooks"
import WarningIcon from "components/Icons/WarningIcon"
import Button from "components/Buttons/Button"
import PopInfo from "components/PopInfo"

type CameraPermissionState = "prompt" | "granted" | "denied" | "loading"
interface ITakePhotoProps {
  onImageChange: (file?: File | undefined) => void
  isNoRemainingScan: boolean
}

const TakePhoto = (props: ITakePhotoProps) => {
  const { onImageChange, isNoRemainingScan } = props

  const backToHome = useBackToHome()

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const [stream, setStream] = useState<MediaStream | null>(null)
  const [cameraPermission, setCameraPermission] =
    useState<CameraPermissionState>("loading")
  const [error, setError] = useState<string | null>(null)
  const [showFlash, setShowFlash] = useState(false)

  useEffect(() => {
    // Check if camera API is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(
        "Camera access is not supported on this device or browser. Please use a modern browser.",
      )
      setCameraPermission("denied")
      return
    }

    // Initialize camera on mount
    initializeCamera()

    // Cleanup on unmount
    return () => {
      stopCamera()
    }
  }, [])

  const initializeCamera = async () => {
    setCameraPermission("loading")
    setError(null)

    try {
      // Try with environment camera first, then fallback to any camera
      let mediaStream: MediaStream | null = null

      mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { exact: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      })

      if (videoRef.current && mediaStream) {
        videoRef.current.srcObject = mediaStream

        // Immediately try to play
        try {
          await videoRef.current.play()
        } catch (playError) {
          // Try again with metadata loaded event
          videoRef.current.onloadedmetadata = async () => {
            try {
              await videoRef.current?.play()
            } catch (retryError) {
              // Silent fail - video won't play
            }
          }
        }
      }

      setStream(mediaStream)
      setCameraPermission("granted")
    } catch (err) {
      let errorMessage =
        "Please enable camera permission in your device settings to take photos."

      if (err instanceof Error) {
        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          errorMessage =
            "Please enable camera permission in your device settings to take photos."
        } else if (err.name === "NotFoundError") {
          errorMessage = "No camera found on this device."
        } else if (err.name === "NotReadableError") {
          errorMessage =
            "Camera is already in use by another application. Please close other apps using the camera."
        } else if (err.name === "OverconstrainedError") {
          errorMessage = "Can not access the rear camera, please try again."
        }
      }

      setError(errorMessage)
      setCameraPermission("denied")
    }
  }

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      setStream(null)
    }
  }

  const capturePhoto = () => {
    const video = videoRef.current
    const canvas = canvasRef.current

    if (!video || !canvas || !stream) {
      return
    }

    // Ensure video has valid dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      return
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Draw the current video frame to canvas
    const ctx = canvas.getContext("2d")
    if (!ctx) {
      return
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Convert to base64 image
    const imageData = canvas.toDataURL("image/jpeg", 0.9)

    // Create File object from data URL
    const file = dataURLtoFile(imageData, "meal-photo.jpg")

    // Trigger visual feedback with flash
    triggerCaptureEffect(file, true)
  }

  const triggerCaptureEffect = (file: File, shouldFlash: boolean) => {
    if (shouldFlash) {
      // 1. Flash effect for camera capture (150ms)
      setShowFlash(true)
      setTimeout(() => {
        setShowFlash(false)
        // Show preview after flash completes
        onImageChange(file)
      }, 150)
    } else {
      // For file upload, show preview immediately without flash
      onImageChange(file)
    }
  }

  const handleClose = () => {
    stopCamera()
    backToHome()
  }

  const handleRetry = () => {
    setError(null)
    initializeCamera()
  }

  const isDisabledCapture = isNoRemainingScan || !stream
  const isDisabledFileInput = isNoRemainingScan

  return (
    <Wrapper>
      <VideoElement
        ref={videoRef}
        autoPlay
        playsInline
        muted
        aria-label="Camera preview"
      />

      <HiddenCanvas ref={canvasRef} />

      {/* Flash effect overlay - only for camera capture */}
      <FlashOverlay $visible={showFlash} />

      {/* Loading overlay */}
      {/* {cameraPermission === "loading" && (
        <LoadingContainer>
          <CustomLoading />
        </LoadingContainer>
      )} */}

      {/* Error overlay */}
      {cameraPermission === "denied" && error && (
        <ErrorContainer>
          <WarningIcon />
          <ErrorTitle>Camera Access Required</ErrorTitle>
          <div>
            <ErrorText>{error}</ErrorText>
            <ErrorText>
              You can still choose an image from your library instead.{" "}
            </ErrorText>
          </div>

          <Button
            style={{ width: 140, alignSelf: "center" }}
            $type="transparent"
            $borderLess
            onClick={handleRetry}
          >
            Try Again
          </Button>
        </ErrorContainer>
      )}

      <AnimatedHeader onClose={handleClose} />

      <CaptureControls
        onCapture={capturePhoto}
        isDisabledCapture={isDisabledCapture}
        isDisabledFileInput={isDisabledFileInput}
        onImageChange={onImageChange}
      />

      <PopInfo
        isOpen={isNoRemainingScan}
        title="Daily limit reached"
        confirmText="Back to Diary"
        onConfirm={backToHome}
      >
        <div style={{ textAlign: "center" }}>
          Looks like you’ve hit today’s upload limit. Please try again tomorrow!
        </div>
      </PopInfo>
    </Wrapper>
  )
}

export default TakePhoto
